import type {
    CreateExtensionArgument,
    Extension,
    ExtensionAPI,
    ResourceLoaderArgs,
    ResourceLoaderDefinition
} from '@wix/document-manager-core'
import _ from 'lodash'
import type {
    ClientSpecMap,
    BuilderEditorClientSpecMapEntry,
    EditorClientSpecMapEntry
} from '@wix/document-services-types'
import {RESOURCE_TYPES, BUILDER_TYPES} from '../../constants/constants'
import type {AppsRuntimeDataServer, TransformedAppRuntimeData} from './componentsLoaderUtils'
import {getOverrideId} from '../../utils/resourceLoaderUtils'
import type {PlatformExtensionAPI} from '../platform'
import {ReportableError} from '@wix/document-manager-utils'

export interface ComponentsResourceLoaderExtensionAPI extends ExtensionAPI {
    componentsResourceLoader: {
        loadComponents(): Promise<TransformedAppRuntimeData[]>
    }
}

const INTERACTIONS = {
    NO_APPS_FOUND: 'components-loader-no-apps-found'
}

export const createExtension = ({
    environmentContext,
    experimentInstance,
    logger
}: CreateExtensionArgument): Extension => {
    let mergingComponentsPromise: Promise<void> | null = null

    const createResourceLoader = (args: ResourceLoaderArgs): ResourceLoaderDefinition[] => {
        const {dal, pointers, extensionAPI} = args

        const loadComponents = async (appDefIds: string[]): Promise<TransformedAppRuntimeData[]> => {
            const clientSpecMap = dal.get(pointers.rendererModel.getClientSpecMap()) as ClientSpecMap
            if (!clientSpecMap) {
                throw new Error('Client spec map is not found')
            }

            const appDefinitionIds: {appDefinitionId: string; version?: string}[] = []
            const overrideIds: string[] = []

            appDefIds.forEach(appDefId => {
                const app = Object.values(clientSpecMap).find(
                    appEntry => (appEntry as BuilderEditorClientSpecMapEntry).appDefinitionId === appDefId
                ) as BuilderEditorClientSpecMapEntry

                if (app) {
                    const version = app.appFields?.installedVersion
                    appDefinitionIds.push({
                        appDefinitionId: appDefId,
                        ...(version && {version})
                    })

                    const overrideId = getOverrideId(dal, pointers, appDefId)
                    if (overrideId) {
                        overrideIds.push(overrideId)
                    }
                }
            })

            if (appDefinitionIds.length === 0) {
                logger.interactionStarted(INTERACTIONS.NO_APPS_FOUND)
                return []
            }

            const uniqueOverrideIds = _.uniq(overrideIds)
            const serverFacade = environmentContext.serverFacade as AppsRuntimeDataServer
            const componentsData = await serverFacade.getComponentsByAppIds(
                appDefinitionIds,
                uniqueOverrideIds.length > 0 ? uniqueOverrideIds : undefined
            )
            return componentsData
        }

        const mergeComponents = (componentsData: TransformedAppRuntimeData[]) => {
            for (const appRuntimeData of componentsData) {
                if (!appRuntimeData.appId || !appRuntimeData.components?.length) {
                    continue
                }

                const clientSpecMapEntryPointer = pointers.rendererModel.getClientSpecMapEntryByAppDefId(
                    appRuntimeData.appId
                )

                const existingEntry = _.cloneDeep(dal.get(clientSpecMapEntryPointer))

                if (!existingEntry) {
                    continue
                }

                const receivedComponents = appRuntimeData.components ?? []

                const nonErcComponents = (existingEntry.components ?? []).filter(
                    (component: any) =>
                        component.type !== BUILDER_TYPES.EDITOR_REACT_COMPONENT &&
                        component.type !== BUILDER_TYPES.EDITOR_CONTEXT_PROVIDER &&
                        component.type !== BUILDER_TYPES.EDITOR_FUNCTION_LIBRARY
                )

                existingEntry.components = [...receivedComponents, ...nonErcComponents]

                if (appRuntimeData.appInstallationData?.installationFlow) {
                    existingEntry.appType = appRuntimeData.appInstallationData.installationFlow
                }

                dal.set(clientSpecMapEntryPointer, existingEntry)
            }
        }

        const getAvailableResources = () => {
            const clientSpecMap = dal.get(pointers.rendererModel.getClientSpecMap()) as ClientSpecMap
            if (!clientSpecMap) {
                return []
            }

            const clientSpecMapAppIds = _.uniq(
                Object.values(clientSpecMap)
                    .map(appSpec => (appSpec as EditorClientSpecMapEntry).appDefinitionId)
                    .filter(Boolean)
            )

            return _.uniq(clientSpecMapAppIds)
        }

        const getEditorAndHybridResources = () => {
            const clientSpecMap = dal.get(pointers.rendererModel.getClientSpecMap()) as ClientSpecMap

            if (!clientSpecMap) {
                return []
            }

            const clientSpecMapAppIds = _.uniq(
                Object.values(clientSpecMap)
                    .filter(appSpec => {
                        const isEditorOrHybrid = ['Editor', 'Hybrid'].includes(
                            (appSpec as EditorClientSpecMapEntry).appType
                        )
                        const appDefId = (appSpec as EditorClientSpecMapEntry).appDefinitionId
                        const hasOverrideId = appDefId && !!getOverrideId(dal, pointers, appDefId)
                        return (
                            (experimentInstance.isOpen('dm_overrideBuilderComponents') && isEditorOrHybrid) ||
                            hasOverrideId
                        )
                    })
                    .map(appSpec => (appSpec as EditorClientSpecMapEntry).appDefinitionId)
                    .filter(Boolean)
            )

            return _.uniq(clientSpecMapAppIds)
        }

        const loadAndMergeComponents = async (appDefIds: string[]): Promise<void> => {
            const componentsData = await loadComponents(appDefIds)
            mergeComponents(componentsData)

            const {platform} = extensionAPI as PlatformExtensionAPI
            if (platform?.getClientSpecMapRefreshCallbacks) {
                const csmRefreshCallbacks = platform.getClientSpecMapRefreshCallbacks()
                csmRefreshCallbacks.forEach(csmRefreshCallback => {
                    try {
                        csmRefreshCallback()
                    } catch (error: any) {
                        logger.captureError(
                            new ReportableError({
                                errorType: 'csmRefreshCallbackError',
                                message: error.message,
                                extras: {originalError: JSON.stringify(error)}
                            })
                        )
                    }
                })
            }
        }

        const isOpened = () =>
            experimentInstance.isOpen('dm_componentsResourceLoader') &&
            pointers.rendererModel.isBuilderComponentModel() &&
            dal.get(pointers.rendererModel.isBuilderComponentModel())

        const load = async (appDefIds: string[]) => {
            if (!isOpened()) {
                return
            }
            mergingComponentsPromise = loadAndMergeComponents(appDefIds)
            await mergingComponentsPromise
        }

        const loadAll = async () => {
            if (!isOpened()) {
                return
            }
            if (!mergingComponentsPromise) {
                const filteredAppDefIds = getEditorAndHybridResources()
                mergingComponentsPromise = loadAndMergeComponents(filteredAppDefIds)
            }
            await mergingComponentsPromise
        }

        const initAvailableResources = async () => {
            if (!isOpened()) {
                return
            }
            loadAll() // no await on purpose - see initialize()
        }

        return [
            {
                resourceType: RESOURCE_TYPES.COMPONENTS,
                load,
                loadAll,
                getAvailableResources,
                initAvailableResources
            }
        ]
    }

    const createExtensionAPI = (): ComponentsResourceLoaderExtensionAPI => {
        return {
            componentsResourceLoader: {
                loadComponents: async () => {
                    throw new Error('loadComponents should be implemented in the resource loader context')
                }
            }
        }
    }

    const initialize = async () => {
        if (mergingComponentsPromise) {
            await mergingComponentsPromise
        }
    }

    return {
        name: 'componentsResourceLoader',
        dependencies: new Set(['documentServicesModel']),
        createResourceLoader,
        createExtensionAPI,
        initialize
    }
}
